import React from 'react';
import { Typography, styled } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDateFormat, DATE_FORMATS } from '../../context/DateFormatContext';
import { 
  formatShamsiDate,
  formatGregorianDate
} from '../../utils/dateUtils';
// Use reliable Hijri formatter from moment-hijri setup
import { formatHijriDate as formatHijriDateMoment } from '../../utils/momentSetup';

// RTL-aware Typography component
const RTLTypography = styled(Typography)(({ theme, isRtl }) => ({
  ...(isRtl && {
    textAlign: 'right',
    direction: 'rtl',
  }),
}));

/**
 * A component that displays dates in the selected calendar format (Shamsi, Qamari, or Gregorian)
 * 
 * @param {Object} props - Component properties
 * @param {string|Date} props.date - The date to display
 * @param {string} props.formatPattern - Optional custom format pattern
 * @param {Object} props.typographyProps - Props to pass to the Typography component
 * @returns {JSX.Element} - The formatted date display component
 */
const LocalizedDate = ({ 
  date, 
  formatPattern = null, 
  typographyProps = {}, 
  showTime = false,
  ...rest 
}) => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'dari' || i18n.language === 'ps';
  const { dateFormat } = useDateFormat();
  
  if (!date) {
    return (
      <RTLTypography {...typographyProps} isRtl={isRtl}>
        -
      </RTLTypography>
    );
  }
  
  let formattedDate = '-';
  
  try {
    // Ensure we have a properly parsed date
    const parsedDate = date instanceof Date ? date : new Date(date);
    
    // Log the date being formatted and the selected calendar format
    console.log('Formatting date:', {
      originalDate: date,
      parsedDate,
      isValid: !isNaN(parsedDate.getTime()),
      selectedFormat: dateFormat,
      showTime: showTime
    });
    
    // Only proceed if we have a valid date
    if (!isNaN(parsedDate.getTime())) {
      // Format the basic date first
      if (dateFormat === DATE_FORMATS.SHAMSI) {
        formattedDate = formatShamsiDate(parsedDate, isRtl, false);
      } 
      else if (dateFormat === DATE_FORMATS.QAMARI) {
        formattedDate = formatHijriDateMoment(parsedDate, isRtl);
      } 
      else {
        formattedDate = formatGregorianDate(parsedDate, isRtl, false);
      }
      
      // Explicitly add time if showTime is true
      if (showTime) {
        let hours = parsedDate.getHours();
        const minutes = parsedDate.getMinutes().toString().padStart(2, '0');
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12;
        hours = hours ? hours : 12; // the hour '0' should be '12'
        const timeStr = ` ${hours}:${minutes} ${ampm}`;
        formattedDate = `${formattedDate}${timeStr}`;
      }
      
      // Log the result
      console.log('Formatted date result:', formattedDate);
      
      // Safety check - if we got anything suspicious, use the Gregorian format as fallback
      if (formattedDate === '-' || formattedDate.includes('undefined') || 
          formattedDate.includes('NaN') || !formattedDate.includes('/')) {
        console.warn('Invalid formatted date, using fallback', { formattedDate });
        formattedDate = formatGregorianDate(parsedDate, isRtl);
      }
    }
  } catch (error) {
    console.error('Error formatting date in LocalizedDate:', error);
    
    // Try to provide a sensible fallback
    try {
      const fallbackDate = new Date(date);
      if (!isNaN(fallbackDate.getTime())) {
        formattedDate = fallbackDate.toLocaleDateString();
      }
    } catch (e) {
      // If all else fails, just display the date as is
      formattedDate = String(date);
    }
  }
  
  return (
    <RTLTypography {...typographyProps} isRtl={isRtl} {...rest}>
      {formattedDate}
    </RTLTypography>
  );
};

export default LocalizedDate; 